import { useSearchContext } from "../contexts/SearchContext";
import { HotelPageResponse, Rate } from "../../../backend/src/models/rateHawkModels/hotelPageResponseModel";
import { useState, useEffect, useRef, useMemo } from "react";
import { fetchHotelDataInfo, fetchHotelPage, preBookHotel } from "../api-client";
import { HotelDataInfo } from "../../../backend/src/models/rateHawkModels/hotelDataInfo";
import { AiFillStar } from "react-icons/ai";
import SearchSummaryBar from "../components/SearchSummaryBar";
import Breadcrumbs from "../components/BreadcrumbsComponent";
import React from "react";
import GoogleMapComponent from "../components/GoogleMapComponent";
import HotelImageGallery from "../utils/HotelImageGallery";
import TripAdvisorRating from "../components/TripAdvisor/TripAdvisorComponent";
import HotelDetails from "../components/HotelDetails";
import RoomsAndPrice from "../components/RoomsAndPriceComponent";
import PageLoaderComponent from "../components/Common/PageLoader";
import GuestInfoForm from "../forms/GuestInfoForm/GuestInfoForm";
import { Link, useLocation } from "react-router-dom";
import { useHotelContext } from "../contexts/HotelContext";
import CheckinCheckoutInfo from "../components/CheckinCheckoutInfo";
import ContactDetailCard from "../components/ContactDetailCard";
import BlogSliderHome from "../components/Blogs/BlogSliderHome";
import PaidOnTheSpotSection from "../components/PaidOnTheSpotComponent";
import IncludedHotelServicesSection from "../components/IncludedHotelServicesSection";

interface HotelDetailsPageProps {
  hotelId: string;
}

const HotelDetailsPage: React.FC<HotelDetailsPageProps> = ({ hotelId }) => {
  const search = useSearchContext();
  const location = useLocation();
  const [hotel, setHotel] = useState<HotelPageResponse | null>(null);
  const [hotelDataInfo, setHotelDataInfo] = useState<HotelDataInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [showMap, setShowMap] = useState(false);
  const [featuredImage, setFeaturedImage] = useState<string | null>(null);
  const [showAllRooms, setShowAllRooms] = useState(false);
  // const targetRef = useRef<HTMLDivElement | null>(null);
  const { saveHotelValues, saveRoomAndRate, roomCode, roomName, roomType, roomPrice, includedBySupplier } = useHotelContext();

  const handleToggleShowAllRooms = () => {
    setShowAllRooms(!showAllRooms);
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const checkIn = new Date(params.get('checkIn') || '');
    const checkOut = new Date(params.get('checkOut') || '');
    const guests = params.get('guests') ? JSON.parse(params.get('guests') || '[]') : [];
    const roomCount = parseInt(params.get('roomCount') || '1');
    const nights = parseInt(params.get('nights') || '0');
    const vacationType = params.get('vacationType') || '';
    const hotelId = params.get('hotelId') || '';
    const regionId = params.get('regionId') || '';
    const residency = params.get('residency') || 'gb';
    const hotelName = params.get('hotelName') || '';

    search.saveSearchValues(
      search.destination,
      checkIn,
      checkOut,
      guests,
      roomCount,
      nights,
      vacationType,
      hotelId,
      regionId,
      residency,
      hotelName
    );
  }, [location.search]);

  const fetchHotelPageDetails = async () => {
    const params = {
      checkin: search.checkIn.toISOString().split("T")[0],
      checkout: search.checkOut.toISOString().split("T")[0],
      residency: search.residency || "gb",
      language: "en",
      guests: search.guests.map((g: any) => ({
        adults: g.adults,
        children: g.children || [],
      })),
      id: hotelId,
      currency: "GBP",
    };

    //console.log("Fetch Hotel Page Params:", params);

    try {
      const hotelPageDetails = await fetchHotelPage(params);
      //console.log("Hotel Page Details:", hotelPageDetails);
      setHotel(hotelPageDetails);

      if (!hotelPageDetails?.data.hotels || hotelPageDetails.data.hotels.length === 0) {
        saveHotelValues(
          '',
          'Not available for Selected Dates',
          hotelDataInfo?.data.name || '',
          ' - ',
          '0.00',
          ' - ',
          '0.00',
          ' - ',
          [],
          ' - '
        );
        saveRoomAndRate(null);
      } else if (roomCode === "" || hotelId != hotelPageDetails?.data.hotels[0].id || roomPrice === roomPrice) {
        const excludedTaxes =
          hotelPageDetails?.data.hotels[0].rates[0].payment_options.payment_types[0].tax_data?.taxes?.filter(
            (tax) => tax.included_by_supplier === false
          ) || [];
          const preBookResponse = await preBookHotel({
            hash: hotelPageDetails?.data.hotels[0].rates[0].book_hash || "",
            price_increase_percent: 0,
          });
          const markedUpPrice = (parseFloat(preBookResponse?.data.hotels[0].rates[0].payment_options.payment_types[0]?.amount || '0') * 1.15);
        saveHotelValues(
          //hotelPageDetails?.data.hotels[0].rates[0].book_hash ?? '',
          preBookResponse?.data.hotels[0].rates[0].book_hash ?? '',
          //hotelPageDetails?.data.hotels[0].rates[0].room_data_trans.main_name ?? '',
          preBookResponse?.data.hotels[0].rates[0].room_name ?? '',
          hotelDataInfo?.data.name ?? '',
          //hotelPageDetails?.data.hotels[0].rates[0].meal_data.value ?? '',
          preBookResponse?.data.hotels[0].rates[0].meal_data.value ?? '',
          //hotelPageDetails?.data.hotels[0].rates[0].payment_options.payment_types[0].amount ?? '',
          markedUpPrice.toFixed(2).toString(),
          hotelId,
          //hotelPageDetails?.data.hotels[0].rates[0].payment_options.payment_types[0].amount ?? '',
          markedUpPrice.toFixed(2).toString(),
          //hotelPageDetails?.data.hotels[0].rates[0].payment_options.payment_types[0].cancellation_penalties ?? '',
          preBookResponse?.data.hotels[0].rates[0].payment_options.payment_types[0].cancellation_penalties ?? '',
          excludedTaxes,
          hotelPageDetails?.data.hotels[0].rates[0].match_hash ?? '',
        );
        saveRoomAndRate(hotelPageDetails?.data.hotels[0].rates[0] ?? null);
      }

    } catch (error) {
      console.error("Failed to fetch hotel details", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchHotelData = async (params: { id: string; language: string }) => {
    try {
      const hotelDataInfo = await fetchHotelDataInfo(params);
      //console.log("Hotel Data Info:", hotelDataInfo);
      setHotelDataInfo(hotelDataInfo);
      if (hotelDataInfo!.data.images.length > 0) {
        setFeaturedImage(hotelDataInfo!.data.images[0].replace("{size}", "1024x768"));
      }
    } catch (error) {
      console.error("Failed to fetch hotel data info", error);
    }
  };

  useEffect(() => {
    if (!hotel && search.hotelId) {
      let hid;
      if (search.hotelId === hotelId) {
        hid = hotelId;
      } else {
        hid = search.hotelId;
      }
      setLoading(true);
      fetchHotelData({ id: hid, language: "en" });
      fetchHotelPageDetails();
    }
  }, [search.hotelId, hotelId]);

  const searchSummaryBarRef = useRef<{ triggerEditSearchClick: () => void }>(null);

  const handleChangeDatesClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (searchSummaryBarRef.current) {
      searchSummaryBarRef.current.triggerEditSearchClick();
    }
  };

  const toggleMap = () => {
    setShowMap(!showMap);
  };

  const formatImageUrl = (url: string) => url.replace("{size}", "1024x768");

  // const scrollToElement = () => {
  //   if (targetRef.current) {
  //     targetRef.current.scrollIntoView({
  //       behavior: 'smooth',
  //       block: 'start',
  //     });

  //     const elementTop = targetRef.current.getBoundingClientRect().top;
  //     const offset = 150;

  //     window.scrollBy({
  //       top: elementTop - offset,
  //       behavior: 'smooth',
  //     });
  //   }
  // };

  // const getUniqueRates = (hotels: HotelPageResponse["data"]["hotels"] = []) => {
  //   const groupedRates: Record<string, Rate[]> = {};

  //   hotels.forEach((hotel) => {
  //     hotel.rates.forEach((rate) => {
  //       const roomName = rate.room_data_trans.main_name.trim();
  //       const mealPlan = rate.meal_data.value;
  //       const uniqueKey = `${roomName}-${mealPlan}`;

  //       if (!groupedRates[uniqueKey]) {
  //         groupedRates[uniqueKey] = [];
  //       }

  //       // Prevent adding duplicate room rates
  //       if (!groupedRates[uniqueKey].some((r) => r.book_hash === rate.book_hash)) {
  //         groupedRates[uniqueKey].push(rate);
  //       }
  //     });
  //   });

  //   return Object.values(groupedRates).flat();
  // };

  const groupRoomsByType = (hotels: HotelPageResponse["data"]["hotels"] = []) => {
    const groupedRooms: Record<
      string,
      { roomName: string; minPrice: number; rates: Rate[]; }
    > = {};

    hotels.forEach((hotel) => {
      hotel.rates.forEach((rate) => {
        const roomName = rate.room_name.trim();
        //const mealPlan = rate.meal_data.value;
        const price = parseFloat(rate.payment_options.payment_types[0].amount);

        // Use only the room name as the key to ensure grouping
        if (!groupedRooms[roomName]) {
          groupedRooms[roomName] = {
            roomName,
            minPrice: price,
            rates: [],
          };
        }

        // Ensure only unique meal plans are stored
        if (!groupedRooms[roomName].rates.some(r => r.book_hash === rate.book_hash)) {
          groupedRooms[roomName].rates.push(rate);
        }

        // Update the minimum price for this room type
        groupedRooms[roomName].minPrice = Math.min(groupedRooms[roomName].minPrice, price);
      });
    });

    return Object.values(groupedRooms);
  };

  // Use this function inside useMemo
  const groupedRates = useMemo(() => {
    if (!hotel?.data?.hotels) return [];
    return groupRoomsByType(hotel.data.hotels);
  }, [hotel?.data?.hotels]);


  const initialRoomDisplay = 4;
  const roomsToDisplay = showAllRooms ? groupedRates : groupedRates.slice(0, initialRoomDisplay);
  const totalRooms = groupedRates.length;

  // const totalCount = search.adultCount + search.childCount;

  const handleRoomSelect = (room: Rate) => {
    saveRoomAndRate(room);
  };

  const formatDate = (date: any) => {
    const day = date.getDate();
    const suffix =
      day % 10 === 1 && day !== 11
        ? "st"
        : day % 10 === 2 && day !== 12
          ? "nd"
          : day % 10 === 3 && day !== 13
            ? "rd"
            : "th";

    const month = date.toLocaleString("default", { month: "short" });
    const year = date.getFullYear();

    return `${day}${suffix} ${month} ${year}`;
  };

  const CheckIn = formatDate(new Date(search.checkIn));
  const CheckOut = formatDate(new Date(search.checkOut));

  if (loading) {
    return <PageLoaderComponent
      location={hotelDataInfo?.data.name || "Your Destination"}
      checkIn={search.checkIn.toString()}
      checkOut={search.checkOut.toString()}
    />;
  }

  if (!hotelDataInfo) {
    return <div>Failed to load hotel data.</div>;
  }

  const formatResponseContent = (content: string) => {
    return content
      .replace(/\r\n/g, '<br />')
      .replace(/\n/g, '<br />');
  };

  const totalAdults = search.guests.reduce((sum, g) => sum + g.adults, 0);
  const totalChildren = search.guests.reduce((sum, g) => sum + g.children.length, 0);

  return (
    <>
      <div className="space-y-6 md:px-0 lg:px-0 mt-12">
        <SearchSummaryBar
          country={hotelDataInfo.data.region.name || ""}
          type={hotelDataInfo.data.region.type || ""}
          ref={searchSummaryBarRef}
        />
        <div className="px-3 md:px-0">
          <Breadcrumbs />
        </div>

        {/* Mobile */}
        <div className="md:hidden lg:hidden lgx2:hidden">
          <div className="px-3 md:px-0">
            <div className="flex">
              {Array.from({ length: hotelDataInfo.data.star_rating || 0 }).map(
                (_, index) => (
                  <AiFillStar key={index} className="text-yellow-400" />
                )
              )}
            </div>
            <h1 className="text-2xl font-bold">{hotelDataInfo.data.name}</h1>
            <h4 className="flex flex-row text-sm text-gray-500 mb-4 items-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 512 512"
                className="h-3 w-3 mt-1 mr-1"
                fill="currentColor"
              >
                <path
                  d="M256 0C153.755 0 70.573 83.182 70.573 185.426c0 126.888 165.939 313.167 173.004 321.035 6.636 7.391 18.222 7.378 24.846 0 7.065-7.868 173.004-194.147 173.004-321.035C441.425 83.182 358.244 0 256 0zm0 278.719c-51.442 0-93.292-41.851-93.292-93.293S204.559 92.134 256 92.134s93.291 41.851 93.291 93.293-41.85 93.292-93.291 93.292z"
                  fill="#d90062"
                ></path>
              </svg>
              {hotelDataInfo.data.address} -
              <span
                onClick={toggleMap}
                className="text-xs cursor-pointer text-[#0050b8] ml-1 hover:underline"
              >
                Show map
              </span>
            </h4>

            {/* Map Popup */}
            {showMap && (
              <div className="map-popup-container show">
                <div className="map-popup-content">
                  <button
                    onClick={toggleMap}
                    className="map-popup-close-button"
                  >
                    ×
                  </button>
                  <GoogleMapComponent
                    apiKey="YOUR_GOOGLE_MAPS_API_KEY"
                    latitude={hotelDataInfo.data.latitude || 0}
                    longitude={hotelDataInfo.data.longitude || 0}
                    zoom={17}
                  />
                </div>
              </div>
            )}
          </div>
          <div className="md:flex">
            <div className="flex flex-col md:flex-row lg:flex-row gap-4 md:w-[800px] 2xl:w-[1000px]">
              {featuredImage && hotelDataInfo.data.images && (
                <HotelImageGallery
                  images={hotelDataInfo.data.images.map((url) => ({
                    url: formatImageUrl(url),
                    alt: hotelDataInfo.data.name,
                  }))}
                  featuredImage={{
                    url: featuredImage,
                    alt: hotelDataInfo.data.name,
                  }}
                  setFeaturedImage={(image) => setFeaturedImage(image.url)}
                />
              )}
            </div>
            {/* <div className="p-4">
              {hotelDataInfo.data.payment_methods &&
                hotelDataInfo.data.payment_methods.length > 0 && (
                  <WhyThisResort hotel={hotelDataInfo} />
                )}
            </div> */}

            <HotelDetails
              hotelId={hotelId}
              slug={hotelDataInfo.data.id || ""}
              roomDetails={hotelDataInfo.data.description_struct || []}
              facilitiesTitle={`Facilities at ${hotelDataInfo.data.name}`}
              mapTitle={`Map Location for ${hotelDataInfo.data.name}`}
              latitude={hotelDataInfo.data.latitude.toString() || ""}
              longitude={hotelDataInfo.data.longitude?.toString() || ""}
              tourQuery={hotelDataInfo.data.region.name || ""}
              destinationCode={hotelDataInfo.data.region.country_code || ""}
              // hasWhyThisResort={hasWhyThisResort(hotelDataInfo || null)}
              amenities={hotelDataInfo.data.amenity_groups || []}
            />

            <div className="p-4 -mt-10">
              <TripAdvisorRating hotelName={hotelDataInfo.data.name} />
            </div>

            <div className="mt-4 md:w-[800px] 2xl:w-[1000px]" id="target">
              <span className="text-2xl font-bold mx-4 md:mx-0">
                {" "}
                Room Types
              </span>
              {hotel?.data.hotels && hotel.data.hotels.length > 0 ? (
                hotel.data.hotels.every((hotel) => hotel.rates.length === 0) ? (
                  <p className="text-red-700">
                    No rooms are available for the selected dates.
                  </p>
                ) : (
                  <>
                    {roomsToDisplay.map(
                      ({ roomName, minPrice, rates }, index) => (
                        <RoomsAndPrice
                          key={`${index}-${roomName}`}
                          roomName={roomName}
                          minPrice={minPrice}
                          rates={rates}
                          //mealPlans={Array.from(mealPlans)}
                          hotelName={hotelDataInfo?.data.name || ""}
                          totalCount={totalAdults + totalChildren}
                          slug={hotelId}
                          hotelId={hotelId}
                          onSelectRoom={saveRoomAndRate}
                          rate={0}
                          hotel={hotel}
                          hotelDataInfo={hotelDataInfo}
                        />
                      )
                    )}
                    {totalRooms > initialRoomDisplay && (
                      <div className="text-center mt-4">
                        <button
                          className="group relative inline-flex items-center justify-center text-sm font-medium tracking-wide text-[#0050b8] hover:text-blue-700 transition-all duration-200 ease-in-out underline"
                          onClick={handleToggleShowAllRooms}
                        >
                          <span className="mr-2">
                            {showAllRooms
                              ? "Show Less Rooms"
                              : "View More Rooms"}
                          </span>
                          <svg
                            className={`w-3.5 h-3.5 transition-transform duration-200 ${
                              showAllRooms ? "rotate-180" : ""
                            }`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                        </button>
                      </div>
                    )}
                  </>
                )
              ) : (
                <>
                  <div className="container mx-auto p-4 flex justify-center items-center h-[24rem]">
                    <div className="p-4 shadow-md rounded-lg bg-white border-t border-gray-200 justify-between flex flex-col items-center">
                      <div className="flex items-center">
                        <img
                          src="/featureIcons/no-avail.svg"
                          alt="Icon"
                          className="w-40 h-40"
                          width="40"
                          height="40"
                        />
                      </div>
                      <div className="flex flex-col justify-center text-center">
                        <h4 className="text-base font-semibold">
                          We have no rooms available at this property for your
                          requested dates.
                        </h4>
                        <p className="text-sm text-halo-pink mt-2">
                          Don't give up yet – adjust your dates or explore a
                          variety of other incredible hotel options waiting for
                          you.
                        </p>
                        <div className="flex items-center gap-2 mt-4">
                          <button
                            type="button"
                            onClick={handleChangeDatesClick}
                            className="bg-halo-pink text-white h-full py-2 px-4 font-medium hover:bg-pink-800 text-base w-full rounded-lg"
                          >
                            Change dates
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div
              className="flex-[1_1_0%] px-4 md:px-0 mb-6 mt-6 md:mt-0"
              id="inquire"
            >
              <div className="p-6 shadow-lg rounded-lg bg-white max-w-md mx-auto border border-gray-300 mb-6">
                <div className="flex justify-between items-center mb-4">
                  <div className="flex-1 text-center w-5/12">
                    <div className="text-base fon-medium text-gray-900">
                      Check-in
                    </div>
                    <div className="text-sm font-normal text-[#0050b8]">
                      {CheckIn}
                    </div>
                  </div>
                  <div className="benefits-divider w-1/12 bg-gray-300 min-h-full"></div>
                  <div className="flex-1 text-center w-5/12">
                    <div className="text-base fon-medium text-gray-900">
                      Check-out
                    </div>
                    <div className="text-sm font-normal text-[#0050b8]">
                      {CheckOut}
                    </div>
                  </div>
                </div>
                <hr className="mb-2" />
                <div className="flex justify-center items-center mb-2 text-md text-gray-800">
                  <span className="mx-4">
                    <span className="font-normal text-[#0050b8]">
                      {search.nights}
                    </span>
                    <span className="text-sm">
                      {search.nights === 1 ? " Night" : " Nights"}
                    </span>
                  </span>
                  <span className="mx-4">
                    <span className="font-normal text-[#0050b8]">
                      {totalAdults}
                    </span>
                    <span className="text-sm">
                      {totalAdults > 1 ? " Adults" : " Adult"}
                    </span>
                  </span>
                  <span className="mx-4">
                    <span className="font-normal text-[#0050b8]">
                      {totalChildren}
                    </span>
                    <span className="text-sm">
                      {totalChildren === 1 ? " Child" : " Children"}
                    </span>
                  </span>
                </div>
                <hr className="mb-2" />
                <div className="text-base font-medium text-gray-900">
                  Room Name:
                  <br />
                  <span
                    className={`font-normal text-sm ${
                      roomName === "Not available for Selected Dates"
                        ? "text-red-500"
                        : "text-gray-700"
                    }`}
                  >
                    {roomName}
                    {/* {roomName !== "Not available for Selected Dates" && (
                      <a
                        onClick={scrollToElement}
                        className="text-xs mt-2 text-center font-normal text-halo-pink hover:text-blue-500 cursor-pointer"
                      >
                        &nbsp;(Change Room)
                      </a>
                    )} */}
                  </span>
                </div>
                <div className="text-base font-medium text-gray-900 mt-2">
                  Meal Plan:
                  <br />
                  <span className="text-gray-700 font-normal text-sm">
                    {roomType && roomType !== " - "
                      ? roomType
                          .toLowerCase()
                          .replace(/\b\w/g, (char) => char.toUpperCase())
                      : roomType}
                    {/* {roomType && roomType !== " - " && (
                      <a
                        onClick={scrollToElement}
                        className="text-xs mt-2 text-center font-normal text-halo-pink hover:text-blue-500 cursor-pointer"
                      >
                        &nbsp;(Change Plan)
                      </a>
                    )} */}
                  </span>
                </div>
                <div className="text-base font-medium text-gray-900 mt-2 mb-6">
                  Room Count: {search.guests.length}
                </div>
                <div className="mb-2 text-sm font-semibold text-gray-800 text-right px-2 flex justify-between items-center">
                  <span className="text-right w-full">Price:</span>
                  <span className="text-gray-700 font-normal line-through ml-1 min-w-20 inline-block">
                    £ {roomPrice}
                  </span>
                </div>
                <div className="flex justify-end">
                  <div className="flex items-center bg-[#f7fffa] px-2 py-1 mb-2 rounded-md min-w-max text-right">
                    {/* <img src="/htag.svg" alt="Save Icon" className="w-4 h-4 mr-1" /> */}
                    <span className="text-sm font-medium text-gray-700">
                      You save:
                    </span>
                    <span className="text-green-600 font-medium text-sm ml-1 min-w-20 inline-block">
                      - £ {(0).toFixed(2)}
                    </span>
                  </div>
                </div>
                <div className="text-right px-2 flex justify-end items-center">
                  <span className="text-lg font-semibold text-gray-700 mb-0 w-full">
                    Total price:
                  </span>
                  <span className="text-xl font-normal text-[#0050b8] min-w-[110px] inline-block text-right">
                    £ {roomPrice}
                  </span>
                </div>
                {/* <div className="text-right px-2 -mt-2">
                  <span className="text-xs font-normal text-[#0050b8]">
                    Per person
                  </span>
                </div> */}
                <div className="text-right px-2 -mt-1">
                  <span className="text-xs font-normal text-gray-400">
                    Inc. taxes & charges
                  </span>
                </div>
              </div>

              <GuestInfoForm
                price={parseInt(roomPrice).toFixed(2)}
                hotelName={hotelDataInfo.data.name}
                slug={hotelId}
                date={search.checkIn.toString()}
                hotelId={hotelId}
                onSelect={handleRoomSelect}
                book_hash={roomCode}
                hotel={hotelDataInfo}
              />

              {includedBySupplier && includedBySupplier.length > 0 && (
                <div className="shadow-lg rounded-lg bg-white max-w-md mx-auto border-t border-gray-300 mb-4 mt-4">
                  <div className="p-6 mb-2 rounded-md min-w-[220px] text-left shadow-sm">
                    <p className="text-sm font-semibold text-gray-700 border-b border-gray-200 pb-1 mb-1">
                      Not included in the price (on the spot):
                    </p>
                    <ul className="space-y-1">
                      {includedBySupplier.map((tax: any, index: number) => (
                        <li
                          key={index}
                          className="text-sm text-gray-800 flex justify-between"
                        >
                          <span className="truncate">
                            {tax.name
                              .replaceAll("_", " ")
                              .replace(/^\w/, (c: string) => c.toUpperCase())}
                          </span>
                          <span className="ml-2 whitespace-nowrap font-medium">
                            {tax.currency_code} {tax.amount}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>

            <div className="container p-6 shadow-lg rounded-lg bg-white max-w-md mx-auto border-t border-gray-200 mb-6 flex items-center justify-between h-[117px]">
              <div className="icon flex items-center mr-4">
                <img
                  src="/featureIcons/quality.svg"
                  alt="Icon"
                  className="w-10 h-10"
                  width="40"
                  height="40"
                />
              </div>
              <div className="content flex flex-col justify-center mx-4 flex-grow">
                <h4 className="text-lg font-semibold">Price Match Guarantee</h4>
                <p className="text-sm text-gray-800">
                  We are not beaten on price
                </p>
                <Link
                  to="/halo-promise#promise5"
                  className="text-xs text-blue-600 hover:text-blue-800 mt-2"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  See how it works
                </Link>
              </div>
            </div>

            <div className="container p-6 shadow-lg rounded-lg bg-white max-w-md mx-auto border-t border-gray-200 mb-6 flex items-center justify-between h-[117px]">
              <div className="icon flex items-center mr-4">
                <img
                  src="/featureIcons/calen.svg"
                  alt="Icon"
                  className="w-10 h-10"
                  width="40"
                  height="40"
                />
              </div>
              <div className="content flex flex-col justify-center mx-4 flex-grow">
                <h4 className="text-lg font-semibold">Easy Payment Plan</h4>
                <p className="text-sm text-gray-800">
                  Convenient payment arrangements for you
                </p>
                <Link
                  to="/halo-promise#promise2"
                  className="text-xs text-blue-600 hover:text-blue-800 mt-2"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  See how it works
                </Link>
              </div>
            </div>
            <div className="p-4">
              <ContactDetailCard />
            </div>
            <h2 className="mt-8 mb-2 mx-4 md:mx-0">
              Explore, Dream, Discover: Travel Stories & Guides
            </h2>
            <div className="p-4 md:p-0">
              <BlogSliderHome />
            </div>
          </div>
        </div>

        {/*Desktop*/}
        <div className="hidden md:block lg:block lgx2:block">
          <div className="px-3 md:px-0">
            <div className="flex">
              {Array.from({ length: hotelDataInfo.data.star_rating || 0 }).map(
                (_, index) => (
                  <AiFillStar key={index} className="text-yellow-400" />
                )
              )}
            </div>
            <h1 className="text-3xl font-bold">{hotelDataInfo.data.name}</h1>
            <h4 className="flex flex-row text-sm text-gray-500 mb-4 items-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 512 512"
                className="h-3 w-3 mt-1 mr-1"
                fill="currentColor"
              >
                <path
                  d="M256 0C153.755 0 70.573 83.182 70.573 185.426c0 126.888 165.939 313.167 173.004 321.035 6.636 7.391 18.222 7.378 24.846 0 7.065-7.868 173.004-194.147 173.004-321.035C441.425 83.182 358.244 0 256 0zm0 278.719c-51.442 0-93.292-41.851-93.292-93.293S204.559 92.134 256 92.134s93.291 41.851 93.291 93.293-41.85 93.292-93.291 93.292z"
                  fill="#d90062"
                ></path>
              </svg>
              {hotelDataInfo.data.address} -
              <span
                onClick={toggleMap}
                className="text-xs cursor-pointer text-[#0050b8] ml-1 hover:underline"
              >
                Show map
              </span>
            </h4>

            {/* Map Popup */}
            {showMap && (
              <div className="map-popup-container show">
                <div className="map-popup-content">
                  <button
                    onClick={toggleMap}
                    className="map-popup-close-button"
                  >
                    ×
                  </button>
                  <GoogleMapComponent
                    apiKey="YOUR_GOOGLE_MAPS_API_KEY"
                    latitude={hotelDataInfo.data.latitude || 0}
                    longitude={hotelDataInfo.data.longitude || 0}
                    zoom={17}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="flex">
            <div className="flex flex-col md:flex-row lg:flex-row gap-4 mx-auto md:w-[67.7%] 2xl:w-[69.5%] pr-6">
              {featuredImage && hotelDataInfo.data.images && (
                <HotelImageGallery
                  images={hotelDataInfo.data.images.map((url) => ({
                    url: formatImageUrl(url),
                    alt: hotelDataInfo.data.name,
                  }))}
                  featuredImage={{
                    url: featuredImage,
                    alt: hotelDataInfo.data.name,
                  }}
                  setFeaturedImage={(image) => setFeaturedImage(image.url)}
                />
              )}
            </div>

            <div className="flex-[1_1_0%] px-4 md:px-0 mt-6 md:mt-0">
              <div className="mb-6 mx-auto">
                <TripAdvisorRating hotelName={hotelDataInfo.data.name} />
              </div>
              {/* <div>
                {hotelDataInfo.data.payment_methods &&
                  hotelDataInfo.data.payment_methods.length > 0 && (
                    <WhyThisResort hotel={hotelDataInfo} />
                  )}
              </div> */}
              <div>
                <div className="pt-6">
                  <CheckinCheckoutInfo
                    checkInTime={hotelDataInfo.data.check_in_time}
                    checkOutTime={hotelDataInfo.data.check_out_time}
                  />
                </div>
              </div>
            </div>
          </div>

          <HotelDetails
            hotelId={hotelId}
            slug={hotelDataInfo.data.id || ""}
            roomDetails={hotelDataInfo.data.description_struct || []}
            facilitiesTitle={`Facilities at ${hotelDataInfo.data.name}`}
            mapTitle={`Map Location for ${hotelDataInfo.data.name}`}
            latitude={hotelDataInfo.data.latitude.toString() || ""}
            longitude={hotelDataInfo.data.longitude?.toString() || ""}
            tourQuery={hotelDataInfo.data.region.name || ""}
            destinationCode={hotelDataInfo.data.region.country_code || ""}
            //hasWhyThisResort={hasWhyThisResort(hotelDataInfo || null)}
            amenities={hotelDataInfo.data.amenity_groups || []}
          />

          <div className="flex mt-12">
            <div className="md:w-[800px] 2xl:w-[1000px] mr-6" id="inquire2">
              <span className="text-2xl font-bold">Room Types</span>
              {hotel?.data.hotels && hotel.data.hotels.length > 0 ? (
                hotel.data.hotels.every((hotel) => hotel.rates.length === 0) ? (
                  <p className="text-red-700">
                    No rooms are available for the selected dates.
                  </p>
                ) : (
                  <>
                    {roomsToDisplay.map(
                      ({ roomName, minPrice, rates }, index) => (
                        <RoomsAndPrice
                          key={`${index}-${roomName}`}
                          roomName={roomName}
                          minPrice={minPrice}
                          rates={rates}
                          //mealPlans={Array.from(mealPlans)}
                          hotelName={hotelDataInfo?.data.name || ""}
                          totalCount={totalAdults + totalChildren}
                          slug={hotelId}
                          hotelId={hotelId}
                          onSelectRoom={saveRoomAndRate}
                          rate={0}
                          hotel={hotel}
                          hotelDataInfo={hotelDataInfo}
                        />
                      )
                    )}
                    {totalRooms > initialRoomDisplay && (
                      <div className="text-center mt-4">
                        <button
                          className="group relative inline-flex items-center justify-center text-sm font-medium tracking-wide text-[#0050b8] hover:text-blue-700 transition-all duration-200 ease-in-out underline"
                          onClick={handleToggleShowAllRooms}
                        >
                          <span className="mr-2">
                            {showAllRooms
                              ? "Show Less Rooms"
                              : "View More Rooms"}
                          </span>
                          <svg
                            className={`w-3.5 h-3.5 transition-transform duration-200 ${
                              showAllRooms ? "rotate-180" : ""
                            }`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                        </button>
                      </div>
                    )}
                  </>
                )
              ) : (
                <>
                  <div className="container p-6 shadow-md rounded-lg bg-white mx-auto border-t border-gray-200 mt-4 flex items-center justify-between h-[15REM]">
                    <div className="icon flex items-center mr-4 ml-4">
                      <img
                        src="/featureIcons/no-avail.svg"
                        alt="Icon"
                        className="w-40 h-40"
                        width="40"
                        height="40"
                      />
                    </div>
                    <div className="content flex flex-col justify-center mx-4 flex-grow">
                      <h4 className="text-lg font-semibold">
                        We have no rooms available at this property for your
                        requested dates.
                      </h4>
                      <p className="text-sm text-halo-pink">
                        Don't give up yet – adjust your dates or explore a
                        variety of other incredible hotel options waiting for
                        you.
                      </p>
                      <div className="flex items-center gap-2 mt-4">
                        <button
                          type="button"
                          onClick={handleChangeDatesClick}
                          className="bg-halo-pink text-white h-full py-2 px-4 font-medium hover:bg-pink-800 text-md w-[30%] rounded-lg"
                        >
                          Change dates
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              )}

              <IncludedHotelServicesSection
                struct={hotelDataInfo?.data?.metapolicy_struct}
              />

              <PaidOnTheSpotSection
                struct={hotelDataInfo?.data?.metapolicy_struct || ""}
              />

              {hotelDataInfo?.data?.metapolicy_extra_info && (
                <div className="mt-12 mx-4 md:mx-0 bg-white shadow-md rounded-lg border border-gray-200">
                  <div className="bg-gray-100 p-4 rounded-t-lg">
                    <h2 className="text-lg font-semibold text-gray-900">
                      Additional Information
                    </h2>
                  </div>
                  <div
                    className="prose prose-sm md:prose-base max-w-none text-gray-800 p-6"
                    dangerouslySetInnerHTML={{
                      __html: formatResponseContent(
                        hotelDataInfo?.data?.metapolicy_extra_info || ""
                      ),
                    }}
                  />
                </div>
              )}
            </div>

            <div
              className="flex-[1_1_0%] px-4 md:px-0 mb-6 mt-6 md:mt-8"
              id="inquire"
            >
              <div className="p-6 shadow-lg rounded-lg bg-white max-w-md mx-auto border border-gray-300 mb-4">
                <div className="flex justify-between items-center mb-4">
                  <div className="flex-1 text-center w-5/12">
                    <div className="text-base font-medium text-gray-900">
                      Check-in
                    </div>
                    <div className="text-sm font-normal text-[#0050b8]">
                      {CheckIn}
                    </div>
                  </div>
                  <div className="benefits-divider w-1/12 bg-gray-300 min-h-full"></div>
                  <div className="flex-1 text-center w-5/12">
                    <div className="text-base font-medium text-gray-900">
                      Check-out
                    </div>
                    <div className="text-sm font-normal text-[#0050b8]">
                      {CheckOut}
                    </div>
                  </div>
                </div>
                <hr className="mb-2" />
                <div className="flex justify-center items-center mb-2 text-md text-gray-800">
                  <span className="mx-4">
                    <span className="font-normal text-[#0050b8]">
                      {search.nights}
                    </span>
                    <span className="text-sm">
                      {search.nights === 1 ? " Night" : " Nights"}
                    </span>
                  </span>
                  <span className="mx-4">
                    <span className="font-normal text-[#0050b8]">
                      {totalAdults}
                    </span>
                    <span className="text-sm">
                      {totalAdults > 1 ? " Adults" : " Adult"}
                    </span>
                  </span>
                  <span className="mx-4">
                    <span className="font-normal text-[#0050b8]">
                      {totalChildren}
                    </span>
                    <span className="text-sm">
                      {totalChildren === 1 ? " Child" : " Children"}
                    </span>
                  </span>
                </div>
                <hr className="mb-2" />
                <div className="text-base font-medium text-gray-900">
                  Room Name:
                  <br />
                  <span
                    className={`font-normal text-sm ${
                      roomName === "Not available for Selected Dates"
                        ? "text-red-500"
                        : "text-gray-700"
                    }`}
                  >
                    {roomName}
                    {/* {roomName !== "Not available for Selected Dates" && (
                      <a
                        onClick={scrollToElement}
                        className="text-xs mt-2 text-center font-normal text-halo-pink hover:text-blue-500 cursor-pointer"
                      >
                        &nbsp;(Change Room)
                      </a>
                    )} */}
                  </span>
                </div>
                <div className="text-base font-medium text-gray-900 mt-2">
                  Meal Plan:
                  <br />
                  <span className="text-gray-700 font-normal text-sm">
                    {roomType && roomType !== " - "
                      ? roomType
                          .toLowerCase()
                          .replace(/\b\w/g, (char) => char.toUpperCase())
                      : roomType}
                    {/* {roomType && roomType !== " - " && (
                      <a
                        onClick={scrollToElement}
                        className="text-xs mt-2 text-center font-normal text-halo-pink hover:text-blue-500 cursor-pointer"
                      >
                        &nbsp;(Change Plan)
                      </a>
                    )} */}
                  </span>
                </div>
                <div className="text-base font-medium text-gray-900 mt-2 mb-6">
                  Room Count: {search.guests.length}
                </div>
                <div className="mb-2 text-sm font-semibold text-gray-800 text-right px-2 flex justify-between items-center">
                  <span className="text-right w-full">Price:</span>
                  <span className="text-gray-700 font-normal line-through ml-1 min-w-20 inline-block">
                    £ {roomPrice}
                  </span>
                </div>
                <div className="flex justify-end">
                  <div className="flex items-center bg-[#f7fffa] px-2 py-1 mb-2 rounded-md min-w-max text-right">
                    {/* <img src="/htag.svg" alt="Save Icon" className="w-4 h-4 mr-1" /> */}
                    <span className="text-sm font-medium text-gray-700">
                      You save:
                    </span>
                    <span className="text-green-600 font-medium text-sm ml-1 min-w-20 inline-block">
                      - £ {(0).toFixed(2)}
                    </span>
                  </div>
                </div>
                <div className="text-right px-2 flex justify-end items-center">
                  <span className="text-lg font-semibold text-gray-700 mb-0 w-full">
                    Total price:
                  </span>
                  <span className="text-xl font-normal text-[#0050b8] min-w-[110px] inline-block text-right">
                    £ {roomPrice}
                  </span>
                </div>
                {/* <div className="text-right px-2 -mt-2">
                  <span className="text-xs font-normal text-[#0050b8]">
                    Per person
                  </span>
                </div> */}
                <div className="text-right px-2 -mt-1">
                  <span className="text-xs font-normal text-gray-400">
                    Inc. taxes & charges
                  </span>
                </div>

                <hr className="mb-2 mt-4" />
                {/* <div className="flex justify-between items-center mt-4">
                  <span className="relative flex items-center">
                    <div className="text-xs text-[#0050b8]">
                      What if I cancel?
                    </div>
                    <span className="relative inline-flex ml-1 group">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="12"
                        height="12"
                        fill="currentColor"
                        className="bi bi-info-circle cursor-pointer text-gray-500"
                        viewBox="0 0 16 16"
                      >
                        <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16" />
                        <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0" />
                      </svg>
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 w-[250px] mb-2 hidden group-hover:block p-3 bg-gray-800 text-white text-xs rounded-lg shadow-md text-center">
                        {hotel &&
                          hotel.data.hotels.length > 0 &&
                          hotel.data.hotels[0].rates[0].payment_options.payment_types[0].cancellation_penalties.policies.length > 0 ? (
                          <span className="block mb-2">
                            <p className="text-white text-xs">
                              {hotel.data.hotels[0].rates[0].payment_options.payment_types[0].cancellation_penalties.policies[0].start_at}
                            </p>
                          </span>
                        ) : (
                          <span>Free Cancellation</span>
                        )}
                      </div>
                    </span>
                  </span>
                </div> */}

                <GuestInfoForm
                  price={(roomPrice)}
                  hotelName={hotelDataInfo.data.name}
                  slug={hotelId}
                  date={search.checkIn.toString()}
                  hotelId={hotelId}
                  onSelect={handleRoomSelect}
                  book_hash={roomCode}
                  hotel={hotelDataInfo}
                />
              </div>
              {includedBySupplier && includedBySupplier.length > 0 && (
                <div className="shadow-lg rounded-lg bg-white max-w-md mx-auto border-t border-gray-300 mb-4">
                  <div className="p-6 mb-2 rounded-md min-w-[220px] text-left shadow-sm">
                    <p className="text-sm font-semibold text-gray-700 border-b border-gray-200 pb-1 mb-1">
                      Not included in the price (on the spot):
                    </p>
                    <ul className="space-y-1">
                      {includedBySupplier.map((tax: any, index: number) => (
                        <li
                          key={index}
                          className="text-sm text-gray-800 flex justify-between"
                        >
                          <span className="truncate">
                            {tax.name
                              .replaceAll("_", " ")
                              .replace(/^\w/, (c: string) => c.toUpperCase())}
                          </span>
                          <span className="ml-2 whitespace-nowrap font-medium">
                            {tax.currency_code} {tax.amount}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
              <div className="container p-6 shadow-lg rounded-lg bg-white max-w-md mx-auto border-t border-gray-200 mb-6 flex items-center justify-between h-[117px]">
                <div className="icon flex items-center mr-4">
                  <img
                    src="/featureIcons/quality.svg"
                    alt="Icon"
                    className="w-10 h-10"
                    width="40"
                    height="40"
                  />
                </div>
                <div className="content flex flex-col justify-center mx-4 flex-grow">
                  <h4 className="text-lg font-semibold">
                    Price Match Guarantee
                  </h4>
                  <p className="text-sm text-gray-800">
                    We are not beaten on price
                  </p>
                  <Link
                    to="/halo-promise#promise5"
                    className="text-xs text-blue-600 hover:text-blue-800 mt-2"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    See how it works
                  </Link>
                </div>
              </div>
              <div className="container p-6 shadow-lg rounded-lg bg-white max-w-md mx-auto border-t border-gray-200 mb-6 flex items-center justify-between h-[117px]">
                <div className="icon flex items-center mr-4">
                  <img
                    src="/featureIcons/calen.svg"
                    alt="Icon"
                    className="w-10 h-10"
                    width="40"
                    height="40"
                  />
                </div>
                <div className="content flex flex-col justify-center mx-4 flex-grow">
                  <h4 className="text-lg font-semibold">Easy Payment Plan</h4>
                  <p className="text-sm text-gray-800">
                    Convenient payment arrangements for you
                  </p>
                  <Link
                    to="/halo-promise#promise2"
                    className="text-xs text-blue-600 hover:text-blue-800 mt-2"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    See how it works
                  </Link>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-10 space-y-8 px-4 md:px-0">
            <ContactDetailCard />

            <div>
              <h2 className="mb-2">
                Explore, Dream, Discover: Travel Stories & Guides
              </h2>
              <BlogSliderHome />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default HotelDetailsPage;
